import dayjs from 'dayjs';
import isEmpty from 'lodash-es/isEmpty';
import { useCallback, useEffect } from 'react';
import { useRoutes, useLocation, useNavigate } from 'react-router-dom';

import AppLayout from '../components/AppLayoutNew';
import NonAuthLayout from '../components/NonAuthLayout';
import NotificationSSE from '../components/NotificationSSE/NotificationSSE';
import SKUModal from '../components/skuModal/SKUModal';
import TabTitle from '../components/TabTitle/TabTitle';
import {
  StatusActionPipelineContextProvider,
  StatusActionPipelineContextProviderNew,
  useUserContext,
  useWorkspaceContext,
  useWorkspaceSKUContext,
} from '../context';
import RecruiterWidget from '../features/conversationWidget/components/RecruiterWidget';
import { PollingHandler } from '../features/emailSettings/components/twoWayEmailSync/PollingHandler';
import DialogProvider from '../hooks/useDialog';
import DrawerProvider from '../hooks/useDrawer';
import { BLOCKED_USERS } from '../utils/Constants';
import getEncodedQueryParams from '../utils/getEncodedQueryParams';
import publicAllowedUrlPaths from '../utils/publicAllowedUrlPaths';
import {
  getAuthToken,
  getWorkspaceToken,
  mapExtensionUrlWithNewUrl,
  logoutFromAllApps,
  setNavURL,
} from '../utils/token';
import { useConversationWidgetStore } from '../utils/useConversationWidgetStore';

import { adminRoutes } from './admin';
import { protectedRoutes } from './protected';

const RouteChecker = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationParam = location.state as { isAdmin: boolean | null };
  const setSearchQuery = useConversationWidgetStore((state) => state.setSearchQuery);
  const openModal = useConversationWidgetStore((state) => state.updateId);
  const user = useUserContext();
  const workspace = useWorkspaceContext();
  const workspaceUser = user?.workspaceUser?.authorities;
  const workspaceSKU = useWorkspaceSKUContext();
  const isAdmin =
    locationParam?.isAdmin ||
    workspaceUser?.includes('ROLE_ADMIN') ||
    workspaceUser?.includes('ROLE_SUPER_ADMIN');
  const routes = isAdmin ? adminRoutes : protectedRoutes;
  const element = useRoutes([...routes]);

  const isPlanExpiredOrNot = useCallback(async () => {
    if (
      workspaceSKU?.expiryDate &&
      location.pathname !== '/workspace/list' &&
      location.pathname !== '/plan/purchase' &&
      location.pathname !== '/app-settings/plan-details' &&
      location.pathname !== '/help-and-knowledge/faq-payment-integration' &&
      location.pathname !== '/app-settings/my-invoices' &&
      location.pathname !== '/app-settings/automation'
    ) {
      const planExpireDate = workspaceSKU?.expiryDate;
      if (dayjs(planExpireDate).diff(new Date(), 'd') <= -7) {
        if (workspace.owns) {
          navigate('/plan/purchase');
        } else {
          window.location.href = `${
            process.env.REACT_APP_URL
          }workspace/list?date=${getEncodedQueryParams({
            expiryDate: dayjs(planExpireDate).format('dddd, MMMM D, YYYY h:mm A'),
          })}`;
        }
      }
    }
  }, [location.pathname, navigate, workspace.owns, workspaceSKU?.expiryDate]);

  const checkPlanExpiredOrNot = useCallback(async () => {
    if (workspaceSKU?.expiryDate && location.pathname !== '/workspace/list') {
      const planExpireDate = workspaceSKU?.expiryDate;
      if (dayjs(planExpireDate).diff(new Date(), 'd') <= -7) {
        window.location.href = `${
          process.env.REACT_APP_URL
        }workspace/list?date=${getEncodedQueryParams({
          expiryDate: dayjs(planExpireDate).format('dddd, MMMM D, YYYY h:mm A'),
        })}`;
      }
    }
  }, [location.pathname, workspaceSKU?.expiryDate]);

  useEffect(() => {
    if (
      user.userDTO?.email &&
      BLOCKED_USERS.includes(user.userDTO?.email) &&
      location.pathname !== '/workspace/list'
    ) {
      window.location.href = `${process.env.REACT_APP_URL}workspace/list?blocked=true`;
    } else {
      const url = mapExtensionUrlWithNewUrl();
      if (url && location.pathname !== '/workspace/list') {
        if (!isEmpty(getWorkspaceToken())) {
          if (url.candidateId) openModal(url.candidateId);
          navigate(url.value);
        } else if (!url.isCandidateId) {
          setNavURL(url.value);
        }
      }
      isPlanExpiredOrNot();
    }
  }, [
    checkPlanExpiredOrNot,
    isPlanExpiredOrNot,
    location.hash,
    location.pathname,
    navigate,
    openModal,
    user.userDTO?.email,
    workspace.id,
  ]);

  useEffect(() => {
    if (!isAdmin && !publicAllowedUrlPaths.includes(location.pathname) && isEmpty(getAuthToken())) {
      setNavURL(location.pathname);
      logoutFromAllApps(window.location.host.split('.')[0]);
    }
  }, [isAdmin, location.pathname]);

  useEffect(() => {
    setSearchQuery(null);
  }, [location.key, setSearchQuery]);

  const checkURLAndToken = () => {
    if (
      location.pathname === '/workspace/list' ||
      location.pathname === '/workspace/create-workspace' ||
      location.pathname === '/onboarding' ||
      location.pathname === '/workspace-otp' ||
      location.pathname.includes('/data-protection') ||
      location.pathname.includes('/subscription') ||
      location.pathname.includes('/unsubscribe') ||
      location.pathname.includes('/subscribed')
    ) {
      return false;
    }
    return true;
  };

  return (
    <>
      {isAdmin ? (
        <AppLayout isAdmin={true}>{element}</AppLayout>
      ) : (
        <>
          {checkURLAndToken() ? (
            <>
              <StatusActionPipelineContextProvider>
                <StatusActionPipelineContextProviderNew>
                  <DialogProvider>
                    <DrawerProvider>
                      <TabTitle title="Wisestep | Make More Money" />
                      <AppLayout>{element}</AppLayout>
                      <RecruiterWidget />
                    </DrawerProvider>
                  </DialogProvider>
                </StatusActionPipelineContextProviderNew>
              </StatusActionPipelineContextProvider>
              <NotificationSSE />
              <SKUModal />
              <PollingHandler />
            </>
          ) : (
            <NonAuthLayout>{element}</NonAuthLayout>
          )}
        </>
      )}
    </>
  );
};

export default RouteChecker;
